<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useOffers } from '@/composables/useOffers'
import OfferCard from './partials/OfferCard.vue'

const router = useRouter()
const route = useRoute()

const {
  offersStatusOptions,
  discountOptions,
  currencies,
  offerTypeOptions,
  countries,
  isLoading: settingsLoading,
} = useGeneralSettings()

const { offers, pagination, currentPage, updateFilters, goToPage, refetchOffers, isLoading } = useOffers({
  page: 1,
})

//  Filter configuration for search and refinement
const filterFields = computed(() => [
  {
    type: 'input',
    key: 'id',
    label: 'شماره پیشنهاد را وارد کنید',
    title: 'جستجو',
  },
  {
    type: 'range',
    key: 'amount',
    title: 'حجم پیشنهاد',
    rangeKeys: {
      min: 'from_rial_amount',
      max: 'till_rial_amount',
    },
  },
  {
    type: 'range',
    key: 'exchangeRate',
    title: 'نرخ تبدیل',
    rangeKeys: {
      min: 'from_exchange_rate',
      max: 'till_exchange_rate',
    },
  },
  {
    type: 'date',
    key: 'requestDate',
    title: 'تاریخ ',
    rangeKeys: {
      min: 'from_date',
      max: 'till_date',
    },
  },
  {
    type: 'select',
    key: 'type',
    label: 'انتخاب نوع پیشنهاد',
    title: 'نوع پیشنهاد',
    options: offerTypeOptions.value,
  },
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت پیشنهاد',
    title: 'وضعیت پیشنهاد',
    options: offersStatusOptions.value,
  },
  {
    type: 'select',
    key: 'currency',
    label: 'انتخاب ارز',
    title: 'نوع ارز',
    options: currencies.value,
  },

  {
    type: 'select',
    key: 'country',
    label: 'انتخاب کشور',
    title: 'کشور',
    options: countries.value,
  },
  {
    type: 'select',
    key: 'discount',
    label: 'انتخاب تخفیف',
    title: 'تخفیف',
    options: discountOptions.value,
  },
])

// --- Filters Events ---
const onApplyFilters = (values: Record<string, any>) => {
  const payload = { ...values }

  updateFilters(payload)

  const query = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== '' && v !== null && v !== undefined))

  router.push({ path: route.path, query })
}

const onResetFilters = () => {
  updateFilters({})
  goToPage(1)
  router.push({ path: '/offers', query: {} })
}

const totalItems = computed(() => pagination.value?.total || 0)

onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    const { page, ...filters } = route.query

    updateFilters(filters)

    if (page) {
      const pageNumber = Number(page)

      if (!isNaN(pageNumber)) {
        currentPage.value = pageNumber
      }
    }
  }
})
</script>

<template>
    <div class="w-full mb-12">
        <h2 class="text-[22px] text-[#1b325a ]">وضعیت پیشنهادها</h2>
        <!-- 👉 Filters Section -->
        <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-6" />

        <!-- 👉 Offers list and pagination -->
        <div v-if="isLoading" class="text-center py-8 text-gray-500">
            در حال دریافت اطلاعات...
        </div>
        <div v-else>
            <div v-if="offers.length > 0" class="mt-6 flex flex-col gap-6 lg:gap-6">
                <OfferCard v-for="(offer, index) in offers" :key="`card-item-${index}`" :offer="offer"
                    @confirmOffer="refetchOffers" />

                <!-- Pagination -->
                <Pagination v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage"
                    :totalItems="totalItems" @update:currentPage="goToPage" :isAdmin="true" />
            </div>
            <EmptyState v-else :onReset="onResetFilters" />
        </div>
    </div>
</template>
